import { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useSubscription } from '@/hooks/useSubscription';
import { useTheme } from '@/App';
import { PlanCards, LEAD_FEATURES } from '@/components/SubscriptionModal';
import { useCheckout } from '@/hooks/useCheckout';

const PLAN_PRICES: Record<string, { sym: string; lead: string; junior: string }> = {
  USD: { sym: '$', lead: '9.50', junior: '5.50' },
  GBP: { sym: '£', lead: '9.50', junior: '5.50' },
  EUR: { sym: '€', lead: '9.50', junior: '5.50' },
};

// ─── Fake skeleton — shown behind the blur instead of real data ───────────────
// Security: real children never render in the expired state. No real data in the DOM.

function FakeAppSkeleton({ isDark }: { isDark: boolean }) {
  const card  = isDark ? 'bg-white/5'  : 'bg-white/70';
  const line  = isDark ? 'bg-white/10' : 'bg-black/10';
  const bg    = isDark ? 'bg-[#0f1419]' : 'bg-[#E3E8F1]';
  const side  = isDark ? 'bg-white/5'  : 'bg-white/50';

  return (
    <div className={`min-h-screen w-full flex ${bg}`} aria-hidden="true">
      {/* Fake sidebar */}
      <div className={`hidden md:flex w-16 min-h-screen ${side} flex-col items-center py-8 gap-6 shrink-0`}>
        {[...Array(5)].map((_, i) => (
          <div key={i} className={`w-8 h-8 rounded-xl ${line}`} />
        ))}
      </div>

      {/* Fake content area */}
      <div className="flex-1 p-10 space-y-6 overflow-hidden">
        {/* Title bar */}
        <div className={`h-10 w-48 rounded-2xl ${line}`} />
        <div className={`h-4 w-64 rounded-xl ${line} opacity-50`} />

        {/* Top cards row */}
        <div className="grid grid-cols-3 gap-5 pt-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className={`rounded-[2rem] p-8 ${card} space-y-4`}>
              <div className={`h-3 w-24 rounded-full ${line}`} />
              <div className={`h-8 w-36 rounded-xl ${line}`} />
              <div className={`h-3 w-32 rounded-full ${line} opacity-40`} />
            </div>
          ))}
        </div>

        {/* Wide chart card */}
        <div className={`rounded-[2rem] p-8 ${card} space-y-4 h-48`}>
          <div className={`h-3 w-32 rounded-full ${line}`} />
          <div className="flex items-end gap-3 h-24 pt-2">
            {[40, 65, 50, 80, 55, 70, 45, 90, 60, 75, 50, 85].map((h, i) => (
              <div key={i} className={`flex-1 rounded-t-lg ${line} opacity-60`} style={{ height: `${h}%` }} />
            ))}
          </div>
        </div>

        {/* Bottom row */}
        <div className="grid grid-cols-2 gap-5">
          {[...Array(2)].map((_, i) => (
            <div key={i} className={`rounded-[2rem] p-8 ${card} space-y-3 h-40`}>
              <div className={`h-3 w-20 rounded-full ${line}`} />
              <div className={`h-6 w-28 rounded-xl ${line}`} />
              <div className={`h-3 w-36 rounded-full ${line} opacity-40`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  children: React.ReactNode;
}

export function TrialExpiredGate({ children }: Props) {
  const { isTrialExpired, status, loading, refresh } = useSubscription();
  const { isDark, currency } = useTheme();
  const [selectedPlan, setSelectedPlan] = useState<'lead' | 'junior'>('lead');
  const { startCheckout, isLoading, error } = useCheckout();
  const pricing = PLAN_PRICES[currency] ?? PLAN_PRICES['USD'];

  // ── Post-checkout polling ─────────────────────────────────────────────────
  // Stripe redirects back to /?checkout=success before the webhook fires.
  // We detect that param and poll Supabase every 2s until the plan updates.
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [activating, setActivating] = useState(() =>
    new URLSearchParams(window.location.search).get('checkout') === 'success'
  );

  useEffect(() => {
    if (!activating) return;

    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      // refresh() returns the freshly-fetched status — avoids stale closure on `status`
      const freshStatus = await refresh();
      const done = freshStatus === 'active' || attempts >= 20;
      if (done) {
        if (attempts >= 20 && freshStatus !== 'active') {
          Sentry.captureMessage('Post-checkout activation polling timed out', {
            level: 'error',
            extra: { attempts, finalStatus: freshStatus, url: window.location.href },
          });
        }
        clearInterval(pollRef.current!);
        const url = new URL(window.location.href);
        url.searchParams.delete('checkout');
        url.searchParams.delete('plan');
        window.history.replaceState({}, '', url.toString());
        setActivating(false);
      }
    }, 2000);

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activating, refresh]);

  // Show spinner while activating (post-checkout)
  if (activating) {
    return (
      <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#E3E8F1] gap-6 overflow-y-auto">
        <div className="w-10 h-10 rounded-full border-[3px] border-[#4A6CF7]/30 border-t-[#4A6CF7] animate-spin" />
        <div className="text-center">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-[#1A1F36]">Activating your subscription</p>
          <p className="text-[10px] font-bold text-[#1A1F36]/40 mt-1">This only takes a moment…</p>
        </div>
      </div>
    );
  }

  const showGate = !loading && (isTrialExpired || status === 'past_due');

  if (!showGate) return <>{children}</>;

  const overlayBg = isDark
    ? 'bg-black/60 [@supports_not_(backdrop-filter:blur(1px))]:bg-black/85'
    : 'bg-white/40 [@supports_not_(backdrop-filter:blur(1px))]:bg-white/95';
  const cardBg = isDark
    ? 'bg-white/10 border-white/10 [@supports_not_(backdrop-filter:blur(1px))]:bg-[#131820]'
    : 'bg-white/80 border-white [@supports_not_(backdrop-filter:blur(1px))]:bg-white';

  return (
    <>
      <div className="pointer-events-none select-none">
        <FakeAppSkeleton isDark={isDark} />
      </div>

      <div className={`fixed inset-0 z-[100] flex items-center justify-center p-6 backdrop-blur-xl overflow-y-auto ${overlayBg}`}>
        <div className={`${cardBg} border backdrop-blur-2xl rounded-[2.5rem] p-4 sm:p-6 md:p-10 max-w-[95vw] lg:max-w-2xl w-full shadow-2xl`}>

          <div className="inline-flex items-center gap-2 px-3 py-1 bg-black text-white rounded-full w-fit mb-6">
            <span className="text-[10px] font-black uppercase tracking-[0.3em]">TRIAL ENDED</span>
          </div>

          <h2 className={`text-4xl font-black tracking-tight ${isDark ? 'text-white' : 'text-[#1A1F36]'}`}>
            Don't lose visibility on your business.
          </h2>
          <p className={`text-[11px] font-bold uppercase tracking-[0.2em] mt-2 mb-8 ${isDark ? 'text-white/40' : 'text-[#1A1F36]/40'}`}>
            Subscribe to keep access to your earnings tracking, invoice history, tax estimates — and more.
          </p>

          <PlanCards selectedPlan={selectedPlan} onSelect={setSelectedPlan} currency={currency} />

          {error && (
            <p className="text-[10px] font-bold text-red-500 mt-4 text-center">{error}</p>
          )}

          <button
            onClick={() => startCheckout(selectedPlan)}
            disabled={isLoading}
            className={`w-full mt-6 rounded-full py-4 font-black uppercase tracking-[0.2em] text-[10px] transition-colors active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'bg-white text-[#1A1F36] hover:bg-white/90' : 'bg-black text-white hover:bg-gray-900'}`}
          >
            {isLoading
              ? 'Redirecting to Stripe...'
              : `Subscribe to ${selectedPlan === 'lead' ? 'Lead' : 'Junior'} — ${pricing.sym}${selectedPlan === 'lead' ? pricing.lead : pricing.junior}/month`}
          </button>

          <p className={`text-[10px] font-bold text-center uppercase tracking-[0.2em] mt-3 ${isDark ? 'text-white/30' : 'text-[#1A1F36]/30'}`}>
            Secured by Stripe
          </p>
        </div>
      </div>
    </>
  );
}
